/* eslint-disable no-unsafe-optional-chaining */
import { Box, Text } from '@quintoandar-tokko/jopijs';
import { useId, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import style from './ToolTip.module.scss';

const HIDE_DELAY_MS = 150;

export default function ToolTip({
  title,
  extraContent = null,
  children,
  place = 'top', // eslint-disable-line no-unused-vars
  outSideModal = false, // eslint-disable-line no-unused-vars
  className = '',
  tipClassName = '',
  interactive = false,
  keyboard = false,
  onMouseEnter,
  onMouseLeave,
  ...props
}) {
  const [hover, setHover] = useState(false);
  const tipId = useId();
  const fromPointer = useRef(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const refTrigger = useRef(null);
  const refTooltip = useRef(null);
  const hideTimeout = useRef(null);

  const cancelHide = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
  };

  const close = () => {
    cancelHide();
    setHover(false);
  };

  // Shared by both "leaving the trigger" and "leaving the tip" so the pointer
  // gets the same grace period crossing the gap in either direction.
  const scheduleHide = () => {
    cancelHide();
    hideTimeout.current = setTimeout(() => setHover(false), HIDE_DELAY_MS);
  };

  const handleMouseDown = () => {
    if (keyboard) fromPointer.current = true;
  };

  const handleMouseUp = () => {
    fromPointer.current = false;
  };

  // A click focuses the trigger after mouse down. Ignore that focus so the tip
  // does not reopen once the pointer leaves. Tab focus has no preceding click.
  const handleFocus = () => {
    if (!keyboard || fromPointer.current) return;
    cancelHide();
    setHover(true);
  };

  const handleBlur = () => {
    if (!keyboard) return;
    fromPointer.current = false;
    setHover(false);
  };

  const handleKeyDown = (event) => {
    if (!keyboard) return;
    if (event.key === 'Escape') setHover(false);
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      cancelHide();
      setHover(true);
    }
  };

  const handleMouseEnter = (e) => {
    cancelHide();
    onMouseEnter?.(e);
    setHover(true);
  };

  const handleMouseLeave = (e) => {
    fromPointer.current = false;
    onMouseLeave?.(e);

    if (!interactive) {
      setHover(false);
      return;
    }

    // Give the pointer time to travel from the trigger to the portaled tip.
    scheduleHide();
  };

  const calculatePosition = () => {
    if (!refTrigger.current) return;

    const triggerRect = refTrigger.current.getBoundingClientRect();
    const spacing = 4;

    // Center horizontally over the trigger element
    const centerX = triggerRect.left + triggerRect.width / 2;

    // Calculate vertical position
    let top;
    if (refTooltip.current) {
      const tooltipRect = refTooltip.current.getBoundingClientRect();
      top = triggerRect.top - tooltipRect.height - spacing;
    } else {
      // Fallback: estimate height
      top = triggerRect.top - 32 - spacing;
    }

    // Ensure tooltip stays in viewport
    if (top < 8) {
      top = triggerRect.bottom + spacing;
    }

    setPosition({ top, left: centerX });
  };

  // Calculate position when tooltip becomes visible
  useEffect(() => {
    if (hover) {
      // Initial calculation
      calculatePosition();

      // Recalculate after a frame to get accurate dimensions
      requestAnimationFrame(() => {
        calculatePosition();
      });

      // Add scroll listener to recalculate on scroll
      const handleScroll = () => calculatePosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
    return undefined;
  }, [hover]);

  useEffect(() => cancelHide, []);

  return (
    <>
      <Box
        {...props}
        role={keyboard ? 'button' : props.role}
        tabIndex={keyboard ? 0 : props.tabIndex}
        aria-expanded={keyboard ? hover : undefined}
        aria-describedby={
          [props['aria-describedby'], keyboard && hover ? tipId : null]
            .filter(Boolean)
            .join(' ') || undefined
        }
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={[style.container, className].filter(Boolean).join(' ')}
        ref={refTrigger}
      >
        {children}
      </Box>

      {hover &&
        (title || extraContent) &&
        typeof document !== 'undefined' &&
        createPortal(
          <Box
            id={keyboard ? tipId : undefined}
            ref={refTooltip}
            className={[style.tip, tipClassName].filter(Boolean).join(' ')}
            onMouseEnter={interactive ? cancelHide : undefined}
            onMouseLeave={interactive ? scheduleHide : undefined}
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform: 'translateX(-50%)',
              zIndex: 1000000,
            }}
          >
            {title && (
              <Text variant="body.fontSize12" className={style.tipText}>
                {title}
              </Text>
            )}
            {extraContent &&
              (typeof extraContent === 'function'
                ? extraContent({ close })
                : extraContent)}
          </Box>,
          document.body
        )}
    </>
  );
}
